/**
 * dcent-app-swap-demos.js — 앱 페이지 §3 스왑 3스텝 화면의 연출 제어.
 *
 * 마크업은 `snippets/dcent-app-{discover,insight,swap}-demo.liquid`,
 * 외형은 `dcent-app-swap-demos.css`.
 * 원본: `20260827 homepage_전달용/src/components/` 의 같은 이름 컴포넌트 3종.
 *
 * 셋 다 "보일 때만 재생 · 벗어나면 리셋 · 단계는 data-* 로만 표현" 이라는 같은 뼈대를 쓰므로
 * 한 파일에 담고 단계표만 다르게 준다. 모양은 전부 CSS 가 갖는다 — 여기서는 상태만 바꾼다.
 *
 * ⚠️ **reduced-motion 이면 각자 알맞은 정지 화면을 그린다.** 셋의 정지 지점이 다르다:
 *   · Discover · 인사이트 → 맨 위(핵심 정보가 위에 있다)
 *   · 스왑           → 견적 완료(빈 입력창을 남기면 "스왑이 된다"는 요지가 사라진다)
 *
 * 인사이트의 스크롤 거리만 **실측**이다(콘텐츠 높이 − 보이는 높이). 고정값을 쓰면 화면 크기가
 * PC/모바일로 달라질 때 덜 내려가거나 넘어간다. Discover 는 반대로 특정 지점에서 멈춰야 하는
 * 연출이라 고정값이 맞다.
 *
 * 안전 규약: 대상 없으면 조용히 종료 · 멱등 설치 · 보일 때만 재생 · 이탈 시 리셋.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentAppSwapDemos) {
  window.__dcentAppSwapDemos = true;

  const reduced = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /**
   * 공통 재생기 — `steps`(단계, ms) 를 순서대로 돌며 root 의 data-* 를 바꾼다.
   * apply(root, step) 이 그 단계를 화면에 반영한다.
   */
  const runner = (root, steps, apply, onEnter) => {
    let i = 0;
    let timer = 0;

    const stop = () => {
      window.clearTimeout(timer);
      timer = 0;
    };
    const tick = () => {
      i = (i + 1) % steps.length;
      apply(root, i);
      timer = window.setTimeout(tick, steps[i]);
    };

    const start = () => {
      if (timer) return;
      if (onEnter) onEnter(root);
      root.dataset.playing = "1";
      timer = window.setTimeout(tick, steps[i]);
    };
    const reset = () => {
      stop();
      i = 0;
      root.dataset.playing = "0";
      apply(root, 0);
    };

    apply(root, 0);

    if (!("IntersectionObserver" in window)) {
      start();
      return;
    }
    // threshold 0.15 — 스텝 화면은 카드 안에서 크게 잘려 0.4 를 못 넘는 배치가 있다
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? start() : reset()),
      { threshold: 0.15 },
    );
    io.observe(root);
  };

  /* ── 스텝 1: Discover 피드 ──────────────────────────────────────────
   * 대기 → 아래로 → 위로 → 탭 → 완성 유지. 마지막 구간이 긴 것은 아코디언이 넘어가기 전에
   * 결과를 끝까지 보여주기 위해서다. */
  const DISCOVER = [300, 1000, 1000, 350, 1350];
  const DISCOVER_PHASE = ["idle", "down", "up", "tap", "idle"];

  const setupDiscover = (root) => {
    if (reduced()) {
      root.dataset.phase = "idle";
      return;
    }
    runner(root, DISCOVER, (el, i) => {
      el.dataset.phase = DISCOVER_PHASE[i];
    });
  };

  /* ── 스텝 2: 토큰 인사이트 ─────────────────────────────────────────
   * 한 바퀴 9초 — 이 화면을 감싸는 아코디언의 전환 주기와 맞춘 값이다. */
  const INSIGHT = [1000, 2900, 350, 650, 4100];
  const INSIGHT_PHASE = ["idle", "down", "tap", "down", "up"];

  const setupInsight = (root) => {
    if (reduced()) {
      root.dataset.phase = "idle";
      return;
    }
    const clip = root.querySelector("[data-insight-clip]");
    const content = root.querySelector("[data-insight-content]");

    // 스크롤 거리 실측 — 재생 시작 직전에 잰다(그때는 레이아웃이 확정돼 있다)
    const measure = (el) => {
      if (!clip || !content) return;
      const depth = Math.max(0, content.offsetHeight - clip.offsetHeight);
      el.style.setProperty("--dcent-ain-depth", `${depth}px`);
    };

    runner(
      root,
      INSIGHT,
      (el, i) => {
        el.dataset.phase = INSIGHT_PHASE[i];
      },
      measure,
    );
  };

  /* ── 스텝 3: Swap & Bridge ────────────────────────────────────────
   * 0 빈 상태 · 1 "5" · 2 "50" · 3 견적 검색 · 4 견적 완료. 마지막이 긴 것은 위와 같은 이유다. */
  const SWAP = [500, 180, 300, 800, 2220];

  /** 겹쳐 둔 값 중 이 단계에 맞는 하나만 켠다. */
  const paintSwap = (root, step) => {
    root.dataset.step = String(step);
    const amtAt = step === 0 ? 0 : step === 1 ? 1 : 2;
    root.querySelectorAll("[data-amt-at]").forEach((n) => {
      n.dataset.on = Number(n.dataset.amtAt) === amtAt ? "1" : "0";
    });
    const quoteAt = step >= 4 ? 1 : 0;
    root.querySelectorAll("[data-quote-at]").forEach((n) => {
      n.dataset.on = Number(n.dataset.quoteAt) === quoteAt ? "1" : "0";
    });
  };

  const setupSwap = (root) => {
    if (reduced()) {
      paintSwap(root, 4); // 견적 완료 — 빈 입력창을 남기지 않는다
      return;
    }
    runner(root, SWAP, paintSwap);
  };

  const KINDS = [
    ["[data-app-discover]", setupDiscover],
    ["[data-app-insight]", setupInsight],
    ["[data-app-swap]", setupSwap],
  ];

  const init = () => {
    KINDS.forEach(([sel, setup]) => {
      document.querySelectorAll(sel).forEach((root) => {
        if (root.dataset.swapDemoReady === "1") return; // 멱등
        root.dataset.swapDemoReady = "1";
        setup(root);
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
